import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useMutation } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import { Button, Container } from 'components/elements';
import { confirmReferenceResponse } from 'graphql/queries/reference-log';
import { PageHeader, PageShell, PageTitle } from 'containers/groupsView/groupsView.styled';

type SubmitState = 'idle' | 'loading' | 'success' | 'error';

export default function ReferenceResponsePage() {
  const { search } = useLocation();
  const history = useHistory();
  const [state, setState] = useState<SubmitState>('idle');
  const [errorText, setErrorText] = useState('');
  const [confirmResponse] = useMutation(confirmReferenceResponse);

  const parsed = useMemo(() => qs.parse(search), [search]);
  const logId = useMemo(() => String(parsed.logId || '').trim(), [parsed.logId]);
  const response = useMemo(() => Number(parsed.response), [parsed.response]);
  const isValid = Boolean(logId) && Number.isFinite(response);

  useEffect(() => {
    let cancelled = false;

    if (!isValid) {
      setState('error');
      setErrorText('Invalid link. Missing or invalid logId/response query params.');
      return;
    }

    setState('loading');
    confirmResponse({
      variables: {
        logId,
        response,
      },
    })
      .then(() => {
        if (!cancelled) {
          setState('success');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState('error');
          setErrorText(err instanceof Error ? err.message : 'Failed to submit reference response.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [confirmResponse, isValid, logId, response]);

  return (
    <Container>
      <PageShell>
        <PageHeader>
          <PageTitle>Reference Response</PageTitle>
        </PageHeader>
        {state === 'loading' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Spin />
            <span>Submitting your response...</span>
          </div>
        )}
        {state === 'success' && (
          <Alert
            type="success"
            showIcon
            message="Thanks! Your reference response has been submitted."
            style={{ marginBottom: 16 }}
          />
        )}
        {state === 'error' && (
          <Alert type="error" showIcon message={errorText || 'Failed to submit response.'} style={{ marginBottom: 16 }} />
        )}
        <Button type="primary" onClick={() => history.push('/')}>
          Back to home
        </Button>
      </PageShell>
    </Container>
  );
}
